import { RubyGemMark } from '../../components/RubyGemMark';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, FileText, FolderPlus, Plus, Edit2, Trash2,
  CheckCircle, RefreshCw, Search, Send,
  ChevronDown, ChevronRight, Save, X,
  Upload, FileSignature, ClipboardCheck, ClipboardList,
  Eye, ThumbsUp, ThumbsDown, Filter, Inbox, FileType,
  LayoutGrid, List as ListIcon, Calendar, UserX, Briefcase,
} from 'lucide-react';
import SearchableDropdown from '../../components/SearchableDropdown';
import { toastService } from '../../services/toastService';
import { staffApi, StaffMember, StaffRole } from '../../services/staffApi';
import {
  staffDocumentTemplates,
  staffOnboardingTemplates,
  staffDocumentRequests,
  staffMemberDocuments,
  staffDocumentUploads,
  DocumentTemplate,
  OnboardingTemplate,
  DocumentRequest,
  DocumentCategory,
  DocumentTemplateType,
  CATEGORY_LABELS,
} from '../../services/staffDocumentService';
import { StaffPage, StaffSubtabs } from '../../components/staff/StaffChrome';
import TemplateViewModal from '../../components/staff/TemplateViewModal';
import QuickFieldList from '../../components/staff/QuickFieldList';
import AcknowledgmentEditor from '../../components/staff/AcknowledgmentEditor';
import EmployeeProgressPanel from '../../components/staff/EmployeeProgressPanel';
import AssignWorkflowModal from '../../components/staff/AssignWorkflowModal';
import AddStepDropdown from '../../components/staff/AddStepDropdown';
import { TEMPLATE_TYPE_LABEL, templateMeta, templateTypeLabel } from '../../components/staff/templateTypeMeta';
import { RubyLoader } from '../../components/RubyLoader';
import { ResetMfaButton } from '../../components/security/ResetMfaButton';
import InviteStaffModal, { InviteStaffInput } from '../../components/staff/InviteStaffModal';
import { isLockedMember, canResetMfaFor, inviteThenRefresh } from './staffRowRules';
import { useAuth } from '../../contexts/AuthContext';
import { useStaffAccess } from '../../hooks/useStaffAccess';

/**
 * STAFF MANAGEMENT — RubyTill's OWN internal staff (PlatformAdminUserPool),
 * ported from platform-app/pages/TeamManagement.tsx.
 *
 * Tabs:
 *   staff     — roster (staffApi), invite / resend-invite, onboarding progress
 *   templates — document-template CRUD (upload / acknowledgment / form / fillable_doc)
 *   workflows — onboarding-workflow builder
 *   review    — submitted-request verification queue
 *
 * NOT tenant employees — no tenant, POS, location, commission or training concepts.
 */

const ACCEPTED_FILE_TYPES = [
  { value: 'application/pdf', label: 'PDF' },
  { value: 'image/jpeg', label: 'JPG' },
  { value: 'image/png', label: 'PNG' },
];

type TabType = 'staff' | 'templates' | 'workflows' | 'review';

const INPUT =
  'w-full px-3.5 py-2.5 bg-white border border-stone-300 rounded-lg text-sm text-stone-900 placeholder-stone-400 focus:outline-none focus:border-admin-primary transition-colors';

// Status badge colors: invited=blue, active=green, suspended=orange, terminated=red.
const STATUS_BADGE: Record<string, string> = {
  invited: 'bg-blue-100 text-blue-800 border border-blue-200',
  active: 'bg-emerald-100 text-emerald-800 border border-emerald-200',
  suspended: 'bg-amber-100 text-amber-800 border border-amber-200',
  terminated: 'bg-red-100 text-red-800 border border-red-200',
};
const STATUS_LABEL: Record<string, string> = {
  invited: 'Invited', active: 'Active', suspended: 'Suspended', terminated: 'Terminated',
};
const STATUS_DOT: Record<string, string> = {
  invited: 'bg-blue-400', active: 'bg-emerald-400', suspended: 'bg-amber-400', terminated: 'bg-red-400',
};

interface OnboardingCell { inProgress: boolean; pct: number | null }

const fmtDay = (d?: string | number | null): string => {
  if (!d) return '';
  const dt = typeof d === 'string' ? new Date(d + (d.length === 10 ? 'T00:00:00' : '')) : new Date(d);
  return isNaN(dt.getTime()) ? '' : dt.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const StaffManagementPage: React.FC = () => {
  const { email: myEmail } = useAuth();
  const { levels, isOwner } = useStaffAccess();
  const navigate = useNavigate();

  // Tab state
  const [activeTab, setActiveTab] = useState<TabType>('staff');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);

  // Roster state
  const [staff, setStaff] = useState<StaffMember[]>([]);
  const [roles, setRoles] = useState<StaffRole[]>([]);
  const [view, setView] = useState<'list' | 'tiles'>('list');
  // staffId -> onboarding summary. Missing/failed lookups degrade to NOT onboarding.
  const [onboardingMap, setOnboardingMap] = useState<Record<string, OnboardingCell>>({});
  // Terminated staff live in a separate, collapsed section.
  const [showTerminated, setShowTerminated] = useState(false);

  // Invite modal
  const [showInvite, setShowInvite] = useState(false);
  const [resendingId, setResendingId] = useState<string | null>(null);

  // Templates state
  const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
  const [viewTemplate, setViewTemplate] = useState<DocumentTemplate | null>(null);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<DocumentTemplate | null>(null);
  const [templateToDelete, setTemplateToDelete] = useState<DocumentTemplate | null>(null);
  const [templateForm, setTemplateForm] = useState({
    name: '',
    description: '',
    category: 'other' as DocumentCategory,
    isRequired: true,
    hasExpiry: false,
    acceptedTypes: ['application/pdf'] as string[],
    templateType: 'upload' as string,
    disclaimerText: '',
  });

  // Form Builder (form templates)
  const [showFormBuilder, setShowFormBuilder] = useState(false);
  const [editingFormTemplate, setEditingFormTemplate] = useState<DocumentTemplate | null>(null);

  // Type picker (single "+ New Template" entry point)
  const [showTypePicker, setShowTypePicker] = useState(false);

  // Acknowledgment editor
  const [showAckEditor, setShowAckEditor] = useState(false);
  const [editingAckTemplate, setEditingAckTemplate] = useState<DocumentTemplate | null>(null);

  // Fillable document
  const [showFillableModal, setShowFillableModal] = useState(false);
  const [fillableForm, setFillableForm] = useState({ name: '', description: '', category: 'tax' as DocumentCategory });
  const [fillableFile, setFillableFile] = useState<File | null>(null);
  const [fillableFileKey, setFillableFileKey] = useState<string | null>(null);
  const [fillableUploading, setFillableUploading] = useState(false);
  const [fillableSaving, setFillableSaving] = useState(false);
  const [fillableError, setFillableError] = useState<string | null>(null);

  // Workflows state
  const [workflows, setWorkflows] = useState<OnboardingTemplate[]>([]);
  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const [editingWorkflow, setEditingWorkflow] = useState<OnboardingTemplate | null>(null);
  const [workflowToDelete, setWorkflowToDelete] = useState<OnboardingTemplate | null>(null);
  const [expandedWorkflows, setExpandedWorkflows] = useState<Set<string>>(new Set());
  const [workflowForm, setWorkflowForm] = useState({
    name: '',
    description: '',
    isActive: true,
    documents: [] as { templateId: string; dueDays: number }[],
  });

  // Review queue state
  const [submissions, setSubmissions] = useState<DocumentRequest[]>([]);
  const [submissionFilter, setSubmissionFilter] = useState<'all' | 'pending' | 'submitted' | 'verified' | 'rejected'>('submitted');
  const [viewingSubmission, setViewingSubmission] = useState<DocumentRequest | null>(null);
  const [rejectingRequest, setRejectingRequest] = useState<DocumentRequest | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');

  // Onboarding progress / assignment
  const [selectedStaff, setSelectedStaff] = useState<{ id: string; name: string } | null>(null);
  const [showAssign, setShowAssign] = useState(false);
  const [progressRefreshKey, setProgressRefreshKey] = useState(0);

  // ========================
  // Loaders
  // ========================

  const loadStaff = async () => {
    const [s, r] = await Promise.all([staffApi.staff.list(), staffApi.roles.list()]);
    // The Master Owner is hidden server-side; drop defensively too.
    setStaff(Array.isArray(s?.staff) ? s.staff.filter((m: any) => !m.isOwner) : []);
    setRoles(Array.isArray(r?.roles) ? r.roles : []);
  };

  const loadDocs = async () => {
    const [templatesData, workflowsData] = await Promise.all([
      staffDocumentTemplates.getAll(),
      staffOnboardingTemplates.getAll(),
    ]);
    setTemplates(Array.isArray(templatesData) ? templatesData : []);
    setWorkflows(Array.isArray(workflowsData) ? workflowsData : []);
  };

  const loadSubmissions = async () => {
    const [requestsData] = await Promise.all([
      staffDocumentRequests.getAll({}),
      // Names + template metadata for the queue rows.
      staff.length ? Promise.resolve(null) : loadStaff(),
      templates.length ? Promise.resolve(null) : loadDocs(),
    ]);
    setSubmissions(Array.isArray(requestsData) ? requestsData : []);
  };

  const loadData = async () => {
    setPageLoading(true);
    try {
      if (activeTab === 'staff') {
        await loadStaff();
      } else if (activeTab === 'templates' || activeTab === 'workflows') {
        await loadDocs();
      } else if (activeTab === 'review') {
        await loadSubmissions();
      }
    } catch (error: any) {
      console.error('Error loading data:', error);
      toastService.error(error?.message || 'Failed to load data');
    } finally {
      setPageLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Ensure workflow + template lists are available for the Assign Workflow modal
  // regardless of which tab loaded them (the staff tab does not otherwise load them).
  useEffect(() => {
    if (workflows.length > 0) return;
    loadDocs().catch(() => { /* non-blocking: assign modal will just show no options */ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // After the roster loads, fetch each non-terminated member's onboarding
  // status (no bulk endpoint exists) so we can split Onboarding vs Active.
  // allSettled → one failed lookup never breaks the page; failures stay Active.
  useEffect(() => {
    const targets = staff.filter((m) => m.status !== 'terminated' && m.id && !isLockedMember(m));
    if (targets.length === 0) {
      setOnboardingMap({});
      return;
    }
    let cancelled = false;
    Promise.allSettled(targets.map((m) => staffMemberDocuments.getOnboardingStatus(m.id))).then((results) => {
      if (cancelled) return;
      const next: Record<string, OnboardingCell> = {};
      targets.forEach((m, i) => {
        const r = results[i];
        if (r.status === 'fulfilled' && r.value) {
          const s: any = r.value;
          const wfs: any[] = Array.isArray(s.workflows) ? s.workflows : [];
          const anyIncomplete = wfs.some((w) => (w.progress ?? 100) < 100);
          const inProgress = s.status === 'in_progress' || anyIncomplete;
          const pct = wfs.length
            ? Math.round(wfs.reduce((sum, w) => sum + (typeof w.progress === 'number' ? w.progress : 100), 0) / wfs.length)
            : null;
          next[m.id] = { inProgress, pct };
        } else {
          // Fall back to the roster record's own flag; unknown → Active.
          next[m.id] = { inProgress: m.onboardingStatus === 'in_progress', pct: null };
        }
      });
      setOnboardingMap(next);
    });
    return () => { cancelled = true; };
  }, [staff]);

  // ========================
  // Roster helpers + handlers
  // ========================

  const roleNameById = useMemo(
    () => Object.fromEntries(roles.map((r) => [r.id, r.name])),
    [roles],
  );
  const roleName = (roleId?: string): string => (roleId && roleNameById[roleId]) || '—';
  // Cognito-only pool members (owner / hand-created admins) have no staff row —
  // they render on the roster read-only with a badge instead of a role.
  const memberBadge = (m: StaffMember): string =>
    m.isOwner ? 'Owner' : m.unmanaged ? 'Platform admin' : roleName(m.roleId);

  const staffById = useMemo(
    () => Object.fromEntries(staff.map((m) => [m.id, m])),
    [staff],
  );

  const openInvite = () => setShowInvite(true);

  const handleInvite = (input: InviteStaffInput) => inviteThenRefresh({
    invite: () => staffApi.staff.invite(input),
    refresh: loadStaff,
    close: () => setShowInvite(false),
    success: (msg) => toastService.success(msg),
    info: (msg) => toastService.info(msg),
    email: input.email,
  });

  const handleResendInvite = async (member: StaffMember) => {
    setResendingId(member.id);
    try {
      await staffApi.staff.resendInvite(member.id);
      toastService.success(`Invitation re-sent to ${member.email}`);
    } catch (err: any) {
      toastService.error(err?.message || 'Failed to resend invitation');
    } finally {
      setResendingId(null);
    }
  };

  // Search + sectioning: Onboarding / Active, with Terminated in its own
  // collapsed section (mirrors the source page).
  const matchesSearch = (m: StaffMember) =>
    m.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    m.email?.toLowerCase().includes(searchTerm.toLowerCase());

  const filteredStaff = staff.filter(matchesSearch);
  const onboardingMembers = filteredStaff.filter((m) => m.status !== 'terminated' && onboardingMap[m.id]?.inProgress);
  const activeMembers = filteredStaff.filter((m) => m.status !== 'terminated' && !onboardingMap[m.id]?.inProgress);
  const terminatedMembers = filteredStaff.filter((m) => m.status === 'terminated');
  const terminatedCount = staff.filter((m) => m.status === 'terminated').length;
  const rosterSections: { key: string; title: string; members: StaffMember[] }[] = [
    { key: 'onboarding', title: 'Onboarding', members: onboardingMembers },
    { key: 'active', title: 'Active', members: activeMembers },
  ];

  // ========================
  // Template handlers
  // ========================

  const handleEditTemplate = (template: DocumentTemplate) => {
    setEditingTemplate(template);
    setTemplateForm({
      name: template.name,
      description: template.description || '',
      category: template.category,
      isRequired: template.isRequired,
      hasExpiry: template.hasExpiry,
      acceptedTypes: template.acceptedTypes || [],
      templateType: template.templateType || 'upload',
      disclaimerText: template.disclaimerText || '',
    });
    setShowTemplateModal(true);
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.name.trim()) {
      toastService.error('Template name is required');
      return;
    }
    if (templateForm.templateType === 'acknowledgment' && !templateForm.disclaimerText.trim()) {
      toastService.error('Agreement/Policy text is required for acknowledgment templates');
      return;
    }
    setLoading(true);
    try {
      const dataToSave: any = {
        ...templateForm,
        acceptedTypes: templateForm.templateType === 'upload' ? templateForm.acceptedTypes : [],
        hasExpiry: templateForm.templateType === 'upload' ? templateForm.hasExpiry : false,
        disclaimerText: templateForm.templateType === 'acknowledgment' ? templateForm.disclaimerText : undefined,
      };
      if (editingTemplate) {
        await staffDocumentTemplates.update(editingTemplate.id, dataToSave);
        toastService.success('Template updated');
      } else {
        await staffDocumentTemplates.create(dataToSave);
        toastService.success('Template created');
      }
      await loadDocs();
      setShowTemplateModal(false);
    } catch (error: any) {
      console.error('Error saving template:', error);
      toastService.error(error?.message || 'Failed to save template');
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteTemplate = async () => {
    if (!templateToDelete) return;
    setLoading(true);
    try {
      await staffDocumentTemplates.delete(templateToDelete.id);
      toastService.success('Template deleted');
      setTemplateToDelete(null);
      await loadDocs();
    } catch (error: any) {
      toastService.error(error?.message || 'Failed to delete template');
    } finally {
      setLoading(false);
    }
  };

  // Route a template to the correct editor based on its type.
  // form → QuickFieldList, acknowledgment → AcknowledgmentEditor,
  // fillable_doc/upload/sheet → generic metadata modal.
  const routeEditTemplate = (template: DocumentTemplate) => {
    if (template.templateType === 'form') {
      setEditingFormTemplate(template);
      setShowFormBuilder(true);
      return;
    }
    if (template.templateType === 'acknowledgment') {
      setEditingAckTemplate(template);
      setShowAckEditor(true);
      return;
    }
    handleEditTemplate(template);
  };

  const handleSaveFormTemplate = async (templateData: any) => {
    try {
      const docTemplateData: any = {
        name: templateData?.name || 'Untitled Form',
        description: templateData?.description,
        category: 'onboarding',
        isRequired: true,
        hasExpiry: false,
        acceptedTypes: [] as string[],
        templateType: 'form',
        formFields: (templateData?.fields || []).map((f: any, i: number) => ({
          id: f.id,
          label: f.label,
          type: f.type,
          required: f.required,
          options: f.options,
          placeholder: f.placeholder,
          order: i,
        })),
      };
      if (editingFormTemplate?.id) {
        await staffDocumentTemplates.update(editingFormTemplate.id, docTemplateData);
        toastService.success('Form template updated');
      } else {
        await staffDocumentTemplates.create(docTemplateData);
        toastService.success('Form template created — you can now add it to workflows');
      }
      setShowFormBuilder(false);
      setEditingFormTemplate(null);
      await loadDocs();
    } catch (error: any) {
      console.error('Error saving form template:', error);
      toastService.error(error?.message || 'Failed to save form template');
    }
  };

  const handleSaveAck = async (payload: any) => {
    setLoading(true);
    try {
      const data: any = {
        name: payload.name,
        description: payload.description,
        category: payload.category,
        isRequired: payload.isRequired,
        hasExpiry: false,
        acceptedTypes: [],
        templateType: 'acknowledgment',
        disclaimerText: payload.disclaimerText,
        requiresSignature: payload.requiresSignature,
        requireTypedName: payload.requireTypedName,
        allowAttachResults: payload.allowAttachResults,
      };
      if (editingAckTemplate?.id) {
        await staffDocumentTemplates.update(editingAckTemplate.id, data);
        toastService.success('Acknowledgment updated');
      } else {
        await staffDocumentTemplates.create(data);
        toastService.success('Acknowledgment created');
      }
      setShowAckEditor(false);
      setEditingAckTemplate(null);
      await loadDocs();
    } catch (error: any) {
      console.error('Error saving acknowledgment:', error);
      toastService.error(error?.message || 'Failed to save acknowledgment');
    } finally {
      setLoading(false);
    }
  };

  // Fillable document: upload the chosen blank PDF and capture its key.
  const handleFillableFileChosen = async (file: File | null) => {
    setFillableError(null);
    setFillableFile(file);
    setFillableFileKey(null);
    if (!file) return;
    setFillableUploading(true);
    try {
      const { uploadUrl, key } = await staffDocumentUploads.getUploadUrl(file.name, file.type);
      await staffDocumentUploads.uploadFile(uploadUrl, file, file.type);
      setFillableFileKey(key);
    } catch (err: any) {
      console.error('Error uploading fillable reference file:', err);
      setFillableError(err?.message || 'Failed to upload the blank form. Please try again.');
      setFillableFile(null);
    } finally {
      setFillableUploading(false);
    }
  };

  const handleSaveFillable = async () => {
    setFillableError(null);
    if (!fillableForm.name.trim()) {
      setFillableError('Template name is required.');
      return;
    }
    if (!fillableFile || !fillableFileKey) {
      setFillableError('Please upload the blank form (PDF).');
      return;
    }
    setFillableSaving(true);
    try {
      await staffDocumentTemplates.create({
        name: fillableForm.name.trim(),
        description: fillableForm.description.trim() || undefined,
        category: fillableForm.category,
        isRequired: true,
        hasExpiry: false,
        acceptedTypes: ['application/pdf'],
        templateType: 'fillable_doc',
        referenceFileKey: fillableFileKey,
        referenceFileName: fillableFile.name,
      } as any);
      toastService.success('Fillable document created');
      setShowFillableModal(false);
      await loadDocs();
    } catch (error: any) {
      console.error('Error saving fillable document:', error);
      setFillableError(error?.message || 'Failed to save fillable document.');
    } finally {
      setFillableSaving(false);
    }
  };

  // Type picker → editors
  const startCreateAcknowledgment = () => {
    setShowTypePicker(false);
    setEditingAckTemplate(null);
    setShowAckEditor(true);
  };
  const startCreateWebForm = () => {
    setShowTypePicker(false);
    setEditingFormTemplate(null);
    setShowFormBuilder(true);
  };
  const startCreateFileUpload = () => {
    setShowTypePicker(false);
    setEditingTemplate(null);
    setTemplateForm({
      name: '', description: '', category: 'other', isRequired: true, hasExpiry: false,
      acceptedTypes: ['application/pdf'], templateType: 'upload', disclaimerText: '',
    });
    setShowTemplateModal(true);
  };
  const startCreateFillableDoc = () => {
    setShowTypePicker(false);
    setFillableForm({ name: '', description: '', category: 'tax' });
    setFillableFile(null);
    setFillableFileKey(null);
    setFillableError(null);
    setFillableUploading(false);
    setFillableSaving(false);
    setShowFillableModal(true);
  };

  // ========================
  // Workflow handlers
  // ========================

  const handleAddWorkflow = () => {
    setEditingWorkflow(null);
    setWorkflowForm({ name: '', description: '', isActive: true, documents: [] });
    setShowWorkflowModal(true);
  };

  const handleEditWorkflow = (workflow: OnboardingTemplate) => {
    setEditingWorkflow(workflow);
    setWorkflowForm({
      name: workflow.name,
      description: workflow.description || '',
      isActive: workflow.isActive,
      documents: (workflow.documents || []).map((d) => ({ templateId: d.templateId, dueDays: d.dueDays })),
    });
    setShowWorkflowModal(true);
  };

  const handleSaveWorkflow = async () => {
    if (!workflowForm.name.trim()) {
      toastService.error('Workflow name is required');
      return;
    }
    setLoading(true);
    try {
      const dataToSave: any = {
        ...workflowForm,
        documents: workflowForm.documents.map((d, idx) => ({ ...d, order: idx + 1 })),
      };
      if (editingWorkflow) {
        await staffOnboardingTemplates.update(editingWorkflow.id, dataToSave);
        toastService.success('Workflow updated');
      } else {
        await staffOnboardingTemplates.create(dataToSave);
        toastService.success('Workflow created');
      }
      await loadDocs();
      setShowWorkflowModal(false);
    } catch (error: any) {
      toastService.error(error?.message || 'Failed to save workflow');
    } finally {
      setLoading(false);
    }
  };

  const confirmDeleteWorkflow = async () => {
    if (!workflowToDelete) return;
    setLoading(true);
    try {
      await staffOnboardingTemplates.delete(workflowToDelete.id);
      toastService.success('Workflow deleted');
      setWorkflowToDelete(null);
      await loadDocs();
    } catch (error: any) {
      toastService.error(error?.message || 'Failed to delete workflow');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleWorkflowActive = async (workflow: OnboardingTemplate) => {
    setLoading(true);
    try {
      await staffOnboardingTemplates.update(workflow.id, { isActive: !workflow.isActive } as any);
      toastService.success(`Workflow ${workflow.isActive ? 'deactivated' : 'activated'}`);
      await loadDocs();
    } catch (error: any) {
      toastService.error(error?.message || 'Failed to update workflow');
    } finally {
      setLoading(false);
    }
  };

  const toggleWorkflowExpanded = (workflowId: string) => {
    setExpandedWorkflows((prev) => {
      const next = new Set(prev);
      if (next.has(workflowId)) next.delete(workflowId);
      else next.add(workflowId);
      return next;
    });
  };

  const handleAddDocumentToWorkflow = (templateId: string) => {
    if (workflowForm.documents.find((d) => d.templateId === templateId)) return;
    setWorkflowForm((prev) => ({ ...prev, documents: [...prev.documents, { templateId, dueDays: 7 }] }));
  };
  const handleRemoveDocumentFromWorkflow = (templateId: string) => {
    setWorkflowForm((prev) => ({ ...prev, documents: prev.documents.filter((d) => d.templateId !== templateId) }));
  };
  const handleUpdateDocumentDueDays = (templateId: string, dueDays: number) => {
    setWorkflowForm((prev) => ({
      ...prev,
      documents: prev.documents.map((d) => (d.templateId === templateId ? { ...d, dueDays } : d)),
    }));
  };
  const handleMoveDocument = (index: number, direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= workflowForm.documents.length) return;
    setWorkflowForm((prev) => {
      const docs = [...prev.documents];
      [docs[index], docs[newIndex]] = [docs[newIndex], docs[index]];
      return { ...prev, documents: docs };
    });
  };

  const getTemplateById = (id: string) => templates.find((t) => t.id === id);

  // ========================
  // Review queue handlers
  // ========================

  const handleApproveSubmission = async (request: DocumentRequest) => {
    setLoading(true);
    try {
      await staffDocumentRequests.verify(request.id, 'verify');
      toastService.success('Submission approved');
      await loadSubmissions();
    } catch (err: any) {
      toastService.error(err?.message || 'Failed to approve submission');
    } finally {
      setLoading(false);
    }
  };

  const handleRejectSubmission = (request: DocumentRequest) => {
    setRejectingRequest(request);
    setRejectionReason('');
  };

  const confirmRejectSubmission = async () => {
    if (!rejectingRequest) return;
    setLoading(true);
    try {
      await staffDocumentRequests.verify(rejectingRequest.id, 'reject', rejectionReason || 'No reason provided');
      toastService.success('Submission rejected');
      setRejectingRequest(null);
      await loadSubmissions();
    } catch (err: any) {
      toastService.error(err?.message || 'Failed to reject submission');
    } finally {
      setLoading(false);
    }
  };

  const getRequestStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending: 'bg-amber-100 text-amber-800 border border-amber-200',
      submitted: 'bg-blue-100 text-blue-800 border border-blue-200',
      verified: 'bg-emerald-100 text-emerald-800 border border-emerald-200',
      rejected: 'bg-red-100 text-red-800 border border-red-200',
    };
    return styles[status] || styles.pending;
  };

  const getTemplateTypeIcon = (type?: string) => {
    switch (type) {
      case 'form': return <ClipboardCheck className="w-4 h-4 text-rose-600" />;
      case 'acknowledgment': return <FileSignature className="w-4 h-4 text-amber-600" />;
      case 'upload': return <Upload className="w-4 h-4 text-blue-600" />;
      default: return <FileText className="w-4 h-4 text-stone-500" />;
    }
  };

  const filteredSubmissions = submissions.filter((s) => submissionFilter === 'all' || s.status === submissionFilter);
  const pendingReviewCount = submissions.filter((s) => s.status === 'submitted').length;

  // ========================
  // Roster renderers
  // ========================

  const renderOnboardingChip = (m: StaffMember) => {
    const cell = onboardingMap[m.id];
    if (!cell?.inProgress) return null;
    return (
      <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-admin-primary/15 text-admin-primary border border-admin-primary/30 shrink-0">
        Onboarding{typeof cell.pct === 'number' ? ` ${cell.pct}%` : ''}
      </span>
    );
  };

  // Lost-authenticator reset: anyone signed in and enrolled, never yourself
  // (the server enforces both — this only hides a button that would 403).
  const canResetMfa = (m: StaffMember) => canResetMfaFor(m, myEmail);

  const renderStaffRow = (m: StaffMember) => (
    <div
      key={m.id}
      className={`p-3 sm:p-4 flex items-center gap-3 sm:gap-4 transition-colors ${isLockedMember(m) ? '' : 'hover:bg-stone-50 active:bg-stone-100 cursor-pointer'} ${m.status === 'terminated' ? 'bg-red-50/60' : ''}`}
      onClick={() => { if (!isLockedMember(m)) navigate(`/staff/${m.id}`); }}
    >
      <div className="w-10 h-10 sm:w-11 sm:h-11 rounded-full flex items-center justify-center text-white font-bold text-base shrink-0 bg-gradient-to-br from-admin-primary to-admin-secondary">
        {m.name?.charAt(0)?.toUpperCase() || 'S'}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-1.5 sm:gap-2">
          <p className="font-semibold text-sm sm:text-base text-stone-900 truncate">{m.name}</p>
          <span className={`px-1.5 sm:px-2 py-0.5 text-[10px] sm:text-xs font-medium rounded-full border ${isLockedMember(m) ? 'bg-amber-50 text-amber-700 border-amber-300' : 'bg-stone-100 text-stone-700 border-stone-300'}`}>
            {memberBadge(m)}
          </span>
          {m.title && (
            <span className="hidden sm:inline-flex items-center gap-1 text-[11px] text-stone-500">
              <Briefcase size={11} /> {m.title}
            </span>
          )}
        </div>
        <p className="text-xs sm:text-sm text-stone-600 truncate">{m.email}</p>
        <p className="text-[11px] text-stone-500 mt-0.5 inline-flex items-center gap-1">
          <Calendar size={11} /> {m.hireDate ? `Hired ${fmtDay(m.hireDate)}` : `Added ${fmtDay(m.createdAt) || '—'}`}
          {m.status === 'terminated' && m.terminatedAt ? (
            <span className="text-red-600 font-medium ml-1">· Ended {fmtDay(m.terminatedAt)}</span>
          ) : null}
        </p>
      </div>
      {renderOnboardingChip(m)}
      <span className={`hidden sm:inline-block px-2.5 py-1 text-xs font-medium rounded-full shrink-0 ${STATUS_BADGE[m.status] || STATUS_BADGE.active}`}>
        {STATUS_LABEL[m.status] || m.status}
      </span>
      <span className={`sm:hidden w-2.5 h-2.5 rounded-full shrink-0 ${STATUS_DOT[m.status] || STATUS_DOT.active}`} />
      {m.status === 'invited' && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); handleResendInvite(m); }}
          disabled={resendingId === m.id}
          className="inline-flex items-center gap-1.5 shrink-0 px-2.5 py-2 rounded-lg text-xs font-medium bg-stone-100 text-blue-600 border border-stone-300 hover:bg-stone-200 transition-colors min-h-[44px] disabled:opacity-50"
          title="Resend invitation email"
        >
          {resendingId === m.id ? <RubyGemMark size={14} drawing color="currentColor" /> : <Send size={14} />}
          <span className="hidden sm:inline">Resend invite</span>
        </button>
      )}
      {m.status !== 'terminated' && !isLockedMember(m) && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedStaff({ id: m.id, name: m.name || m.email || 'Staff member' });
            setProgressRefreshKey((k) => k + 1);
          }}
          className="inline-flex items-center gap-1.5 shrink-0 px-2.5 py-2 rounded-lg text-xs font-medium bg-stone-100 text-stone-700 border border-stone-300 hover:bg-stone-200 transition-colors min-h-[44px]"
          title="View onboarding progress"
        >
          <ClipboardCheck size={14} />
          <span className="hidden sm:inline">Onboarding</span>
        </button>
      )}
      {canResetMfa(m) && <ResetMfaButton userId={m.id} name={m.name || m.email} email={m.email} />}
      {!isLockedMember(m) && <ChevronRight size={18} className="text-stone-500 shrink-0" />}
    </div>
  );

  const renderStaffTile = (m: StaffMember) => (
    <div
      key={m.id}
      onClick={() => { if (!isLockedMember(m)) navigate(`/staff/${m.id}`); }}
      className={`bg-white rounded-xl border p-4 transition-colors ${isLockedMember(m) ? '' : 'cursor-pointer hover:border-stone-300'} ${m.status === 'terminated' ? 'border-red-200' : 'border-stone-200'}`}
    >
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold shrink-0 bg-gradient-to-br from-admin-primary to-admin-secondary">
          {m.name?.charAt(0)?.toUpperCase() || 'S'}
        </div>
        <div className="min-w-0 flex-1">
          <div className="font-semibold text-stone-900 truncate">{m.name || '—'}</div>
          <div className="text-xs text-stone-600 truncate">{m.email}</div>
        </div>
        <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium shrink-0 ${STATUS_BADGE[m.status] || STATUS_BADGE.active}`}>
          {STATUS_LABEL[m.status] || m.status}
        </span>
      </div>
      <div className="mt-2 flex items-center gap-2 text-xs text-stone-600 flex-wrap">
        <span className={`px-2 py-0.5 rounded-full font-medium border ${isLockedMember(m) ? 'bg-amber-50 text-amber-700 border-amber-300' : 'bg-stone-100 text-stone-700 border-stone-300'}`}>{memberBadge(m)}</span>
        {m.title && <span className="inline-flex items-center gap-1"><Briefcase size={12} /> {m.title}</span>}
        <span className="inline-flex items-center gap-1">
          <Calendar size={12} /> {m.hireDate ? `Hired ${fmtDay(m.hireDate)}` : `Added ${fmtDay(m.createdAt) || '—'}`}
        </span>
        {renderOnboardingChip(m)}
      </div>
      {m.status === 'terminated' && m.terminatedAt && (
        <div className="mt-2 text-[11px] bg-red-50 border border-red-200 text-red-600 rounded px-2 py-1 inline-flex items-center gap-1">
          Ended {fmtDay(m.terminatedAt)}{m.terminationReason ? ` · ${m.terminationReason}` : ''}
        </div>
      )}
      <div className="mt-3 flex justify-end gap-2">
        {m.status === 'invited' && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); handleResendInvite(m); }}
            disabled={resendingId === m.id}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-stone-100 text-blue-600 border border-stone-300 hover:bg-stone-200 disabled:opacity-50"
          >
            {resendingId === m.id ? <RubyGemMark size={14} drawing color="currentColor" /> : <Send size={14} />} Resend invite
          </button>
        )}
        {m.status !== 'terminated' && !isLockedMember(m) && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setSelectedStaff({ id: m.id, name: m.name || m.email || 'Staff member' });
              setProgressRefreshKey((k) => k + 1);
            }}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium bg-stone-100 text-stone-700 border border-stone-300 hover:bg-stone-200"
            title="View onboarding progress"
          >
            <ClipboardCheck size={14} /> Onboarding
          </button>
        )}
        {canResetMfa(m) && <ResetMfaButton userId={m.id} name={m.name || m.email} email={m.email} compact />}
      </div>
    </div>
  );

  const renderStaffTab = () => (
    <div className="space-y-4">
      {/* Toolbar: search + list/grid toggle */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="relative max-w-md flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
          <input
            type="text"
            placeholder="Search staff members..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`${INPUT} pl-9`}
          />
        </div>
        <div className="flex items-center gap-0.5 bg-white border border-stone-200 rounded-lg p-0.5">
          <button
            onClick={() => setView('list')}
            title="List view"
            className={`px-2.5 py-1.5 rounded-md text-sm font-medium flex items-center gap-1.5 ${view === 'list' ? 'bg-stone-200 text-stone-900' : 'text-stone-600 hover:text-stone-700'}`}
          >
            <ListIcon size={15} /> List
          </button>
          <button
            onClick={() => setView('tiles')}
            title="Grid view"
            className={`px-2.5 py-1.5 rounded-md text-sm font-medium flex items-center gap-1.5 ${view === 'tiles' ? 'bg-stone-200 text-stone-900' : 'text-stone-600 hover:text-stone-700'}`}
          >
            <LayoutGrid size={15} /> Grid
          </button>
        </div>
      </div>

      {rosterSections.some((s) => s.members.length > 0) ? (
        rosterSections.filter((s) => s.members.length > 0).map((section) => (
          <div key={section.key} className="space-y-2">
            <div className="flex items-center gap-2 px-0.5">
              <h3 className="text-sm font-semibold text-stone-700">{section.title}</h3>
              <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-stone-100 text-stone-600">{section.members.length}</span>
            </div>
            {view === 'list' ? (
              <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
                <div className="divide-y divide-stone-200">
                  {section.members.map((m) => renderStaffRow(m))}
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                {section.members.map((m) => renderStaffTile(m))}
              </div>
            )}
          </div>
        ))
      ) : (
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm px-6 py-12 text-center text-stone-600">
          <Users size={48} className="mx-auto mb-4 text-stone-600" />
          <p>{searchTerm ? 'No staff members match your search' : 'No staff members yet'}</p>
          {!searchTerm && <p className="text-xs text-stone-500 mt-1">Click "Invite Staff" to get started</p>}
        </div>
      )}

      {/* Terminated — separate, collapsed section */}
      {!showTerminated ? (
        terminatedCount > 0 && (
          <button
            type="button"
            onClick={() => setShowTerminated(true)}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-dashed border-stone-300 bg-white text-sm font-medium text-stone-600 hover:bg-stone-100 transition-colors min-h-[44px]"
          >
            <UserX size={16} />
            View terminated staff ({terminatedCount})
          </button>
        )
      ) : (
        <div className="space-y-2">
          <div className="flex items-center gap-2 px-0.5">
            <h3 className="text-sm font-semibold text-stone-700">Terminated</h3>
            <span className="px-2 py-0.5 rounded-full text-[11px] font-medium bg-stone-100 text-stone-600">{terminatedMembers.length}</span>
            <button
              type="button"
              onClick={() => setShowTerminated(false)}
              className="ml-auto text-xs font-medium text-stone-500 hover:text-stone-700"
            >
              Hide
            </button>
          </div>
          {terminatedMembers.length === 0 ? (
            <div className="bg-white rounded-xl border border-stone-200 shadow-sm px-6 py-8 text-center text-stone-600 text-sm">
              {searchTerm ? 'No terminated staff match your search' : 'No terminated staff'}
            </div>
          ) : view === 'list' ? (
            <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
              <div className="divide-y divide-stone-200">
                {terminatedMembers.map((m) => renderStaffRow(m))}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {terminatedMembers.map((m) => renderStaffTile(m))}
            </div>
          )}
        </div>
      )}
    </div>
  );

  // ========================
  // Templates tab
  // ========================

  const renderTemplatesTab = () => {
    const typeDetail = (template: DocumentTemplate): string => {
      switch (template.templateType) {
        case 'form':
          return `${template.formFields?.length || 0} fields`;
        case 'fillable_doc':
          return 'blank PDF';
        case 'acknowledgment':
          return 'signature required';
        case 'sheet':
          return 'sheet';
        case 'upload':
        default: {
          const exts = (template.acceptedTypes || [])
            .map((t) => (t.split('/')[1] || t).toUpperCase())
            .filter(Boolean);
          return exts.length ? exts.join('/') : 'any file';
        }
      }
    };

    const renderTemplateCard = (template: DocumentTemplate) => {
      const type = template.templateType || 'upload';
      const meta = templateMeta(type);
      return (
        <div key={template.id} className="bg-white rounded-xl border border-stone-200 shadow-sm p-4 hover:border-stone-300 transition-colors">
          <div className="flex items-start gap-3">
            <div className={`w-10 h-10 rounded-xl flex items-center justify-center text-lg shrink-0 ${meta.tile}`}>
              <span aria-hidden>{meta.icon}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-sm font-semibold text-stone-900 truncate">{template.name}</h3>
                <span className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${meta.badge}`}>
                  {TEMPLATE_TYPE_LABEL[type] || 'Upload'}
                </span>
              </div>
              <p className="text-xs text-stone-600 mt-0.5 truncate">
                {CATEGORY_LABELS[template.category]} · {typeDetail(template)}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 mt-3 pt-3 border-t border-stone-200">
            <button
              onClick={() => setViewTemplate(template)}
              className="flex-1 text-xs font-medium border border-stone-300 rounded-lg py-1.5 hover:bg-stone-100 inline-flex items-center justify-center gap-1 transition-colors text-stone-700"
            >
              <Eye className="w-3.5 h-3.5" /> View
            </button>
            <button
              onClick={() => routeEditTemplate(template)}
              className="flex-1 text-xs font-medium border border-stone-300 rounded-lg py-1.5 hover:bg-stone-100 inline-flex items-center justify-center gap-1 transition-colors text-stone-700"
            >
              <Edit2 className="w-3.5 h-3.5" /> Edit
            </button>
            <button
              onClick={() => setTemplateToDelete(template)}
              className="text-xs font-medium border border-red-200 text-red-600 rounded-lg py-1.5 px-2.5 hover:bg-red-50 inline-flex items-center justify-center transition-colors"
              aria-label={`Delete ${template.name}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      );
    };

    return (
      <div className="space-y-4 sm:space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <p className="text-sm text-stone-600">Define document types that can be requested from staff members.</p>
          <button
            onClick={() => setShowTypePicker(true)}
            className="px-3 sm:px-4 py-2 sm:py-2.5 bg-admin-primary hover:bg-admin-secondary text-white rounded-lg flex items-center gap-1.5 sm:gap-2 text-sm font-medium transition-colors whitespace-nowrap self-start sm:self-auto"
          >
            <Plus size={16} /> New Template
          </button>
        </div>

        {templates.length === 0 ? (
          <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-16 text-center">
            <div className="w-16 h-16 rounded-2xl bg-stone-100 flex items-center justify-center mx-auto mb-5">
              <FileText size={32} className="text-stone-500" />
            </div>
            <h3 className="text-lg font-semibold text-stone-900 mb-2">No templates yet</h3>
            <p className="text-stone-600 max-w-sm mx-auto">Create templates for document uploads, acknowledgments, or custom forms</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
            {templates.map((t) => renderTemplateCard(t))}
          </div>
        )}
      </div>
    );
  };

  // ========================
  // Workflows tab
  // ========================

  const renderWorkflowsTab = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <p className="text-sm text-stone-600">Create onboarding workflows with multiple required documents.</p>
        <button
          onClick={handleAddWorkflow}
          className="px-4 py-2.5 bg-admin-primary hover:bg-admin-secondary text-white rounded-lg flex items-center gap-2 text-sm font-medium transition-colors"
        >
          <Plus size={18} /> Add Workflow
        </button>
      </div>

      {workflows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-stone-200 shadow-sm p-16 text-center">
          <div className="w-14 h-14 rounded-xl bg-admin-primary/15 flex items-center justify-center mx-auto mb-4">
            <FolderPlus size={28} className="text-admin-primary" />
          </div>
          <h3 className="text-lg font-semibold text-stone-900 mb-2">No workflows yet</h3>
          <p className="text-stone-600 max-w-sm mx-auto mb-6">Create workflows to streamline new hire onboarding</p>
          <button
            onClick={handleAddWorkflow}
            className="px-5 py-2.5 bg-admin-primary hover:bg-admin-secondary text-white rounded-lg font-medium transition-colors"
          >
            Create First Workflow
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {workflows.map((workflow) => {
            const isExpanded = expandedWorkflows.has(workflow.id);
            return (
              <div key={workflow.id} className="bg-white rounded-xl border border-stone-200 shadow-sm hover:border-stone-300 transition-colors">
                <div className="p-4">
                  <div className="flex items-center justify-between">
                    <div
                      className="flex items-center gap-3 cursor-pointer flex-1"
                      onClick={() => toggleWorkflowExpanded(workflow.id)}
                    >
                      <div className="w-8 h-8 rounded-lg bg-admin-primary/15 text-admin-primary flex items-center justify-center">
                        <FolderPlus className="w-4 h-4" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <h3 className="font-medium text-stone-900">{workflow.name}</h3>
                          <span className={`px-2 py-0.5 rounded text-[10px] font-medium ${
                            workflow.isActive
                              ? 'bg-emerald-100 text-emerald-800 border border-emerald-200'
                              : 'bg-stone-100 text-stone-500 border border-stone-200'
                          }`}
                          >
                            {workflow.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </div>
                        <div className="flex items-center flex-wrap gap-1.5 mt-0.5">
                          <span className="text-xs text-stone-600">
                            {workflow.documents.length} step{workflow.documents.length !== 1 ? 's' : ''}
                            {workflow.description && ` · ${workflow.description}`}
                          </span>
                          {Array.from(new Set(
                            workflow.documents
                              .map((d) => getTemplateById(d.templateId)?.templateType)
                              .filter((t): t is DocumentTemplateType => !!t),
                          )).map((type) => (
                            <span key={type} className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${templateMeta(type).badge}`}>
                              {templateMeta(type).icon} {templateTypeLabel(type)}
                            </span>
                          ))}
                        </div>
                      </div>
                      {isExpanded
                        ? <ChevronDown className="w-5 h-5 text-stone-500" />
                        : <ChevronRight className="w-5 h-5 text-stone-500" />}
                    </div>
                    <div className="flex items-center gap-1 ml-3">
                      <button
                        onClick={() => handleToggleWorkflowActive(workflow)}
                        className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50 transition-colors"
                        title={workflow.isActive ? 'Deactivate' : 'Activate'}
                      >
                        {workflow.isActive ? <X className="w-4 h-4" /> : <CheckCircle className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => handleEditWorkflow(workflow)}
                        className="p-1.5 rounded-lg text-stone-600 hover:bg-stone-100 transition-colors"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setWorkflowToDelete(workflow)}
                        className="p-1.5 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
                {isExpanded && workflow.documents.length > 0 && (
                  <div className="border-t border-stone-200 bg-stone-50 p-4">
                    <div className="space-y-2">
                      {[...workflow.documents]
                        .sort((a, b) => a.order - b.order)
                        .map((doc, index) => {
                          const template = getTemplateById(doc.templateId);
                          if (!template) return null;
                          return (
                            <div key={doc.templateId} className="flex items-center gap-3 bg-white p-2.5 rounded-lg border border-stone-200">
                              <span className="text-xs font-medium text-stone-500 w-5">{index + 1}</span>
                              <span className={`w-6 h-6 rounded flex items-center justify-center text-xs ${templateMeta(template.templateType).tile}`}>
                                {templateMeta(template.templateType).icon}
                              </span>
                              <span className="flex-1 text-sm text-stone-900">{template.name}</span>
                              <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-stone-100 text-stone-600">
                                {CATEGORY_LABELS[template.category]}
                              </span>
                              <span className="text-xs text-stone-500">{doc.dueDays}d</span>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  // ========================
  // Review queue tab
  // ========================

  const renderReviewTab = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-stone-600">Review and approve submitted forms, documents, and acknowledgments.</p>
        <div className="flex items-center gap-2">
          <Filter size={16} className="text-stone-500" />
          <div style={{ minWidth: 180 }}>
            <SearchableDropdown
              options={[
                { value: 'all', label: 'All Submissions' },
                { value: 'submitted', label: 'Awaiting Review' },
                { value: 'pending', label: 'Pending' },
                { value: 'verified', label: 'Approved' },
                { value: 'rejected', label: 'Rejected' },
              ]}
              value={submissionFilter}
              onChange={(val) => setSubmissionFilter((val || 'all') as any)}
              placeholder="Filter submissions"
              compact
            />
          </div>
          <button
            onClick={() => loadSubmissions().catch((e: any) => toastService.error(e?.message || 'Failed to load submissions'))}
            className="p-1.5 text-stone-600 hover:text-stone-900 hover:bg-stone-100 rounded"
            title="Refresh"
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {filteredSubmissions.length === 0 ? (
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm p-12 text-center">
          <Inbox size={48} className="mx-auto text-stone-600 mb-4" />
          <p className="text-stone-600 mb-2">No submissions found</p>
          <p className="text-sm text-stone-500">
            {submissionFilter === 'submitted'
              ? 'No submissions awaiting review'
              : `No ${submissionFilter === 'all' ? '' : submissionFilter + ' '}submissions`}
          </p>
        </div>
      ) : (
        <div className="bg-white rounded-xl border border-stone-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-stone-50 border-b border-stone-200">
                <tr>
                  <th className="text-left text-xs font-medium text-stone-500 uppercase px-4 py-3">Type</th>
                  <th className="text-left text-xs font-medium text-stone-500 uppercase px-4 py-3">Document</th>
                  <th className="text-left text-xs font-medium text-stone-500 uppercase px-4 py-3">Staff member</th>
                  <th className="text-left text-xs font-medium text-stone-500 uppercase px-4 py-3">Status</th>
                  <th className="text-left text-xs font-medium text-stone-500 uppercase px-4 py-3">Submitted</th>
                  <th className="text-right text-xs font-medium text-stone-500 uppercase px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-200">
                {filteredSubmissions.map((request) => {
                  const member = staffById[request.userId];
                  const template = getTemplateById(request.documentTemplateId);
                  const templateType = template?.templateType || request.templateType || 'upload';
                  return (
                    <tr key={request.id} className="hover:bg-stone-50">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {getTemplateTypeIcon(templateType)}
                          <span className="text-xs text-stone-600 capitalize">{templateType.replace('_', ' ')}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-medium text-stone-900">{request.templateName || template?.name || 'Unknown'}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className="text-stone-700">{member?.name || member?.email || request.userId}</span>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getRequestStatusBadge(request.status)}`}>
                          {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-stone-600">
                        {request.submittedAt
                          ? new Date(request.submittedAt).toLocaleDateString()
                          : request.acknowledgedAt
                            ? new Date(request.acknowledgedAt).toLocaleDateString()
                            : '-'}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          {(templateType === 'form' || templateType === 'acknowledgment') && (
                            <button
                              onClick={() => setViewingSubmission(request)}
                              className="p-1.5 text-stone-600 hover:text-blue-600 hover:bg-blue-50 rounded"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                          )}
                          {request.status === 'submitted' && (
                            <>
                              <button
                                onClick={() => handleApproveSubmission(request)}
                                disabled={loading}
                                className="p-1.5 text-stone-600 hover:text-emerald-600 hover:bg-emerald-50 rounded disabled:opacity-50"
                                title="Approve"
                              >
                                <ThumbsUp className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleRejectSubmission(request)}
                                disabled={loading}
                                className="p-1.5 text-stone-600 hover:text-red-600 hover:bg-red-50 rounded disabled:opacity-50"
                                title="Reject"
                              >
                                <ThumbsDown className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );

  // ========================
  // Modals
  // ========================

  const renderTemplateModal = () => {
    if (!showTemplateModal) return null;
    const isAcknowledgment = templateForm.templateType === 'acknowledgment';
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
        <div className="bg-white border border-stone-200 rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
          <div className="bg-white text-stone-900 p-5 rounded-t-2xl border-b border-stone-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-admin-primary">
                  {isAcknowledgment ? <FileSignature className="w-5 h-5 text-white" /> : <Upload className="w-5 h-5 text-white" />}
                </div>
                <div>
                  <h3 className="text-lg font-semibold">
                    {editingTemplate ? 'Edit ' : 'New '}
                    {isAcknowledgment ? 'Acknowledgment' : 'Upload'}
                  </h3>
                  <p className="text-sm text-stone-600">
                    {isAcknowledgment ? 'Staff member must read and acknowledge' : 'Staff member uploads a document file'}
                  </p>
                </div>
              </div>
              <button onClick={() => setShowTemplateModal(false)} className="p-2 hover:bg-stone-100 rounded-lg transition-colors">
                <X size={20} />
              </button>
            </div>
          </div>

          <div className="p-5 space-y-5">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Template Name *</label>
              <input
                type="text"
                value={templateForm.name}
                onChange={(e) => setTemplateForm((prev) => ({ ...prev, name: e.target.value }))}
                className={INPUT}
                placeholder={isAcknowledgment ? 'e.g., Background Check Authorization' : "e.g., Driver's License"}
              />
            </div>

            {!isAcknowledgment && (
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Description</label>
                <textarea
                  value={templateForm.description}
                  onChange={(e) => setTemplateForm((prev) => ({ ...prev, description: e.target.value }))}
                  rows={2}
                  className={`${INPUT} resize-none`}
                  placeholder="Brief description of what document to upload..."
                />
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Category</label>
              <SearchableDropdown
                options={Object.entries(CATEGORY_LABELS).map(([value, label]) => ({ value, label: label as string }))}
                value={templateForm.category}
                onChange={(val) => setTemplateForm((prev) => ({ ...prev, category: (val || 'other') as DocumentCategory }))}
                placeholder="Select category"
              />
            </div>

            {isAcknowledgment && (
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Agreement / Policy Text *</label>
                <textarea
                  value={templateForm.disclaimerText}
                  onChange={(e) => setTemplateForm((prev) => ({ ...prev, disclaimerText: e.target.value }))}
                  rows={5}
                  className={`${INPUT} resize-none`}
                  placeholder="Enter the full text of the agreement, policy, or authorization that staff members must read and acknowledge..."
                />
                <p className="text-xs text-stone-500 mt-1.5">
                  This text will be displayed to the staff member. They must read and agree before completing.
                </p>
              </div>
            )}

            {!isAcknowledgment && (
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-3">Accepted File Types</label>
                <div className="flex flex-wrap gap-3">
                  {ACCEPTED_FILE_TYPES.map((type) => (
                    <label key={type.value} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={templateForm.acceptedTypes.includes(type.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setTemplateForm((prev) => ({ ...prev, acceptedTypes: [...prev.acceptedTypes, type.value] }));
                          } else {
                            setTemplateForm((prev) => ({ ...prev, acceptedTypes: prev.acceptedTypes.filter((t) => t !== type.value) }));
                          }
                        }}
                        className="w-4 h-4 rounded border-stone-300 bg-white accent-admin-primary"
                      />
                      <span className="text-sm text-stone-700">{type.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="flex items-center gap-6 pt-2">
              <label className="flex items-center gap-2.5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={templateForm.isRequired}
                  onChange={(e) => setTemplateForm((prev) => ({ ...prev, isRequired: e.target.checked }))}
                  className="w-4 h-4 rounded border-stone-300 bg-white accent-admin-primary"
                />
                <span className="text-sm text-stone-700">Required Document</span>
              </label>
              {!isAcknowledgment && (
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={templateForm.hasExpiry}
                    onChange={(e) => setTemplateForm((prev) => ({ ...prev, hasExpiry: e.target.checked }))}
                    className="w-4 h-4 rounded border-stone-300 bg-white accent-admin-primary"
                  />
                  <span className="text-sm text-stone-700">Has Expiry Date</span>
                </label>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 p-5 border-t border-stone-200">
            <button
              onClick={() => setShowTemplateModal(false)}
              className="px-5 py-2.5 bg-white border border-stone-300 text-stone-700 hover:bg-stone-50 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveTemplate}
              disabled={loading}
              className="px-5 py-2.5 bg-admin-primary hover:bg-admin-secondary text-white rounded-lg font-medium flex items-center gap-2 disabled:opacity-50 transition-colors"
            >
              <Save size={16} />
              {editingTemplate ? 'Update' : 'Create'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderWorkflowModal = () => {
    if (!showWorkflowModal) return null;
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
        <div className="bg-white border border-stone-200 rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="bg-white text-stone-900 p-5 rounded-t-2xl border-b border-stone-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-admin-primary flex items-center justify-center">
                  <FolderPlus className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold">{editingWorkflow ? 'Edit Workflow' : 'New Workflow'}</h3>
                  <p className="text-sm text-stone-600">Define the onboarding steps</p>
                </div>
              </div>
              <button onClick={() => setShowWorkflowModal(false)} className="p-2 hover:bg-stone-100 rounded-lg transition-colors">
                <X size={20} />
              </button>
            </div>
          </div>

          <div className="p-5 space-y-5">
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Workflow Name *</label>
              <input
                type="text"
                value={workflowForm.name}
                onChange={(e) => setWorkflowForm((prev) => ({ ...prev, name: e.target.value }))}
                className={INPUT}
                placeholder="e.g., Standard Staff Onboarding"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-stone-700 mb-2">Description</label>
              <textarea
                value={workflowForm.description}
                onChange={(e) => setWorkflowForm((prev) => ({ ...prev, description: e.target.value }))}
                rows={2}
                className={`${INPUT} resize-none`}
                placeholder="Brief description..."
              />
            </div>
            <label className="flex items-center gap-2.5 cursor-pointer">
              <input
                type="checkbox"
                checked={workflowForm.isActive}
                onChange={(e) => setWorkflowForm((prev) => ({ ...prev, isActive: e.target.checked }))}
                className="w-4 h-4 rounded border-stone-300 bg-white accent-admin-primary"
              />
              <span className="text-sm text-stone-700">Active Workflow</span>
            </label>

            <div className="border-t border-stone-200 pt-5">
              <div className="flex items-center justify-between mb-4">
                <label className="block text-sm font-medium text-stone-700">Workflow Steps</label>
                <AddStepDropdown
                  templates={templates.map((t) => ({ id: t.id, name: t.name, templateType: t.templateType }))}
                  addedIds={workflowForm.documents.map((d) => d.templateId)}
                  onSelect={(id) => handleAddDocumentToWorkflow(id)}
                />
              </div>
              {workflowForm.documents.length === 0 ? (
                <div className="text-center py-10 bg-stone-50 rounded-xl border-2 border-dashed border-stone-300">
                  <div className="w-12 h-12 rounded-lg bg-stone-100 flex items-center justify-center mx-auto mb-3">
                    <FileText className="w-6 h-6 text-stone-500" />
                  </div>
                  <p className="text-sm text-stone-700 font-medium">No steps added yet</p>
                  <p className="text-xs text-stone-500 mt-1">Select templates from the dropdown above</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {workflowForm.documents.map((doc, index) => {
                    const template = getTemplateById(doc.templateId);
                    if (!template) return null;
                    return (
                      <div key={doc.templateId} className="flex items-center gap-3 bg-stone-50 p-3 rounded-lg border border-stone-200">
                        <div className="flex flex-col gap-0.5">
                          <button
                            onClick={() => handleMoveDocument(index, 'up')}
                            disabled={index === 0}
                            className="p-0.5 text-stone-500 hover:text-stone-700 disabled:opacity-30 transition-colors"
                          >
                            <ChevronRight className="w-4 h-4 -rotate-90" />
                          </button>
                          <button
                            onClick={() => handleMoveDocument(index, 'down')}
                            disabled={index === workflowForm.documents.length - 1}
                            className="p-0.5 text-stone-500 hover:text-stone-700 disabled:opacity-30 transition-colors"
                          >
                            <ChevronRight className="w-4 h-4 rotate-90" />
                          </button>
                        </div>
                        <span className="text-sm font-medium text-stone-500 w-5">{index + 1}</span>
                        <span className={`w-7 h-7 rounded flex items-center justify-center text-sm ${templateMeta(template.templateType).tile}`}>
                          {templateMeta(template.templateType).icon}
                        </span>
                        <span className="flex-1 text-sm text-stone-900">{template.name}</span>
                        <span className="px-2 py-0.5 rounded text-[10px] font-medium bg-stone-100 text-stone-600">
                          {CATEGORY_LABELS[template.category]}
                        </span>
                        <div className="flex items-center gap-1.5">
                          <input
                            type="number"
                            value={doc.dueDays}
                            onChange={(e) => handleUpdateDocumentDueDays(doc.templateId, parseInt(e.target.value) || 7)}
                            className="w-14 px-2 py-1.5 text-sm bg-white border border-stone-300 rounded-lg text-stone-900 focus:border-admin-primary outline-none text-center"
                            min={1}
                          />
                          <span className="text-xs text-stone-500">days</span>
                        </div>
                        <button
                          onClick={() => handleRemoveDocumentFromWorkflow(doc.templateId)}
                          className="p-1.5 text-stone-500 hover:text-stone-900 hover:bg-stone-200 rounded transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 p-5 border-t border-stone-200">
            <button
              onClick={() => setShowWorkflowModal(false)}
              className="px-5 py-2.5 bg-white border border-stone-300 text-stone-700 hover:bg-stone-50 rounded-lg font-medium transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSaveWorkflow}
              disabled={loading}
              className="px-5 py-2.5 bg-admin-primary hover:bg-admin-secondary text-white rounded-lg font-medium flex items-center gap-2 disabled:opacity-50 transition-colors"
            >
              <Save size={16} />
              {editingWorkflow ? 'Update' : 'Create'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderSubmissionModal = () => {
    if (!viewingSubmission) return null;
    const template = getTemplateById(viewingSubmission.documentTemplateId);
    const member = staffById[viewingSubmission.userId];
    const templateType = template?.templateType || viewingSubmission.templateType || 'upload';
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
        <div className="bg-white border border-stone-200 rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-4 border-b border-stone-200 sticky top-0 bg-white z-10">
            <div className="flex items-center gap-3">
              {getTemplateTypeIcon(templateType)}
              <div>
                <h3 className="text-lg font-bold text-stone-900">{viewingSubmission.templateName || template?.name}</h3>
                <p className="text-sm text-stone-600">
                  Submitted by {member?.name || member?.email || 'Unknown'}
                </p>
              </div>
            </div>
            <button onClick={() => setViewingSubmission(null)} className="p-1 hover:bg-stone-100 rounded text-stone-700">
              <X size={20} />
            </button>
          </div>

          <div className="p-6">
            <div className="mb-6">
              <span className={`px-3 py-1.5 rounded-full text-sm font-medium ${getRequestStatusBadge(viewingSubmission.status)}`}>
                {viewingSubmission.status.charAt(0).toUpperCase() + viewingSubmission.status.slice(1)}
              </span>
              {viewingSubmission.rejectionReason && (
                <p className="mt-2 text-sm text-red-600">Reason: {viewingSubmission.rejectionReason}</p>
              )}
            </div>

            {templateType === 'form' && viewingSubmission.formResponses && (
              <div className="space-y-4">
                <h4 className="font-semibold text-stone-900 border-b border-stone-200 pb-2">Form Responses</h4>
                {template?.formFields?.slice().sort((a: any, b: any) => a.order - b.order).map((field: any) => {
                  const value = viewingSubmission.formResponses?.[field.id];
                  return (
                    <div key={field.id} className="bg-stone-50 border border-stone-200 rounded-lg p-4">
                      <label className="block text-sm font-medium text-stone-600 mb-1">
                        {field.label}
                        {field.required && <span className="text-red-600 ml-1">*</span>}
                      </label>
                      <div className="text-stone-900">
                        {field.type === 'checkbox'
                          ? (value ? 'Yes' : 'No')
                          : value || <span className="text-stone-500 italic">No response</span>}
                      </div>
                    </div>
                  );
                })}
                {(!template?.formFields || template.formFields.length === 0) && (
                  <div className="text-stone-600 italic">
                    <p>Form field definitions not found. Raw responses:</p>
                    <pre className="mt-2 p-3 bg-white border border-stone-200 rounded text-xs overflow-auto text-stone-700">
                      {JSON.stringify(viewingSubmission.formResponses, null, 2)}
                    </pre>
                  </div>
                )}
              </div>
            )}

            {templateType === 'acknowledgment' && (
              <div className="space-y-4">
                <h4 className="font-semibold text-stone-900 border-b border-stone-200 pb-2">Acknowledgment Details</h4>
                {template?.disclaimerText && (
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <label className="block text-sm font-medium text-amber-600 mb-2">Agreement/Policy Text</label>
                    <div className="text-stone-700 text-sm whitespace-pre-wrap">{template.disclaimerText}</div>
                  </div>
                )}
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-emerald-600 mb-3">
                    <CheckCircle className="w-5 h-5" />
                    <span className="font-medium">Acknowledged</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <label className="block text-stone-500 mb-1">Date &amp; Time</label>
                      <span className="text-stone-900">
                        {viewingSubmission.acknowledgedAt
                          ? new Date(viewingSubmission.acknowledgedAt).toLocaleString()
                          : viewingSubmission.submittedAt
                            ? new Date(viewingSubmission.submittedAt).toLocaleString()
                            : 'N/A'}
                      </span>
                    </div>
                    <div>
                      <label className="block text-stone-500 mb-1">Acknowledged By</label>
                      <span className="text-stone-900">
                        {member?.name || member?.email || viewingSubmission.acknowledgedBy || 'N/A'}
                      </span>
                    </div>
                  </div>
                  {viewingSubmission.signature && (
                    <div className="mt-4">
                      <label className="block text-stone-500 mb-1">Signature</label>
                      <div className="text-lg italic text-stone-900">{viewingSubmission.signature}</div>
                    </div>
                  )}
                </div>
              </div>
            )}

            <div className="mt-6 pt-4 border-t border-stone-200 text-sm text-stone-500">
              <div className="flex justify-between flex-wrap gap-2">
                <span>Requested: {viewingSubmission.requestedAt ? new Date(viewingSubmission.requestedAt).toLocaleString() : '—'}</span>
                {viewingSubmission.verifiedAt && (
                  <span>Verified: {new Date(viewingSubmission.verifiedAt).toLocaleString()}</span>
                )}
              </div>
            </div>
          </div>

          {viewingSubmission.status === 'submitted' && (
            <div className="flex justify-end gap-3 p-4 border-t border-stone-200 bg-stone-50">
              <button
                onClick={() => {
                  handleRejectSubmission(viewingSubmission);
                  setViewingSubmission(null);
                }}
                className="px-4 py-2 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 flex items-center gap-2"
              >
                <ThumbsDown size={16} /> Reject
              </button>
              <button
                onClick={() => {
                  handleApproveSubmission(viewingSubmission);
                  setViewingSubmission(null);
                }}
                disabled={loading}
                className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 flex items-center gap-2 disabled:opacity-50"
              >
                <ThumbsUp size={16} /> Approve
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderRejectModal = () => {
    if (!rejectingRequest) return null;
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
        <div className="bg-white border border-stone-200 rounded-xl shadow-xl w-full max-w-md p-6">
          <h3 className="text-lg font-bold text-stone-900 mb-4">Reject Submission</h3>
          <p className="text-sm text-stone-600 mb-4">
            Please provide a reason for rejecting "{rejectingRequest.templateName}". The staff member will be notified.
          </p>
          <textarea
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            placeholder="Enter rejection reason..."
            className={`${INPUT} mb-4 resize-none`}
            rows={3}
          />
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setRejectingRequest(null)}
              className="px-4 py-2 bg-white border border-stone-300 text-stone-700 hover:bg-stone-50 rounded-lg"
            >
              Cancel
            </button>
            <button
              onClick={confirmRejectSubmission}
              disabled={loading}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
            >
              Reject
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Generic confirm modal — never window.confirm.
  const renderConfirm = (
    open: boolean,
    title: string,
    message: React.ReactNode,
    onConfirm: () => void,
    onCancel: () => void,
  ) => {
    if (!open) return null;
    return (
      <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
        <div className="bg-white border border-stone-200 rounded-xl shadow-xl w-full max-w-md p-6">
          <h3 className="text-lg font-bold text-stone-900 mb-2">{title}</h3>
          <p className="text-stone-600 mb-4">{message}</p>
          <div className="flex justify-end gap-2">
            <button
              onClick={onCancel}
              disabled={loading}
              className="px-4 py-2 bg-white border border-stone-300 text-stone-700 hover:bg-stone-50 rounded-lg disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={loading}
              className="px-4 py-2 bg-red-600 text-white hover:bg-red-700 rounded-lg disabled:opacity-50"
            >
              {loading ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ========================
  // Header stats
  // ========================
  const nonTerminated = staff.filter((m) => m.status !== 'terminated');
  const activeCount = staff.filter((m) => m.status === 'active').length;
  const invitedCount = staff.filter((m) => m.status === 'invited').length;
  const onboardingCount = Object.values(onboardingMap).filter((c) => c.inProgress).length;

  const TABS: { id: string; label: string; badge?: number }[] = [
    { id: 'staff', label: 'Staff' },
    { id: 'templates', label: 'Templates' },
    { id: 'workflows', label: 'Workflows' },
    { id: 'review', label: 'Review Queue', badge: pendingReviewCount || undefined },
  ];

  // ========================
  // Main render
  // ========================

  return (
    <StaffPage
      icon={Users}
      title="Team Management"
      subtitle="RubyTill internal staff — roster, document templates & onboarding workflows"
      actions={
        <>
          {/* Stats (desktop) — rendered inside the black header bar */}
          <div className="hidden lg:flex items-center gap-6 mr-3">
            <div className="text-center">
              <div className="text-xl font-bold text-blue-400">{nonTerminated.length}</div>
              <div className="text-[10px] text-white/40 uppercase tracking-wider">Total</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-emerald-400">{activeCount}</div>
              <div className="text-[10px] text-white/40 uppercase tracking-wider">Active</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-rose-400">{onboardingCount}</div>
              <div className="text-[10px] text-white/40 uppercase tracking-wider">Onboarding</div>
            </div>
            <div className="text-center">
              <div className="text-xl font-bold text-white">{templates.length}</div>
              <div className="text-[10px] text-white/40 uppercase tracking-wider">Templates</div>
            </div>
          </div>
          <button
            onClick={loadData}
            className="p-2 rounded-lg hover:bg-white/10 transition-colors text-white/60 hover:text-white"
            title="Refresh"
          >
            {pageLoading ? <RubyGemMark size={18} drawing color="currentColor" /> : <RefreshCw size={18} />}
          </button>
          <button
            onClick={openInvite}
            className="flex items-center gap-2 px-4 py-2 rounded-lg font-medium text-sm transition-colors min-h-[40px] bg-admin-primary text-white hover:bg-admin-secondary"
          >
            <Plus size={16} />
            <span className="hidden sm:inline">Invite Staff</span>
          </button>
        </>
      }
      tabs={<StaffSubtabs tabs={TABS} active={activeTab} onChange={(id) => setActiveTab(id as TabType)} />}
    >
      <div>
        {/* Mobile stats */}
        <div className="md:hidden grid grid-cols-4 gap-2 mb-4">
          <div className="p-3 rounded-xl bg-white border border-stone-200 text-center">
            <div className="text-lg font-bold text-blue-600">{nonTerminated.length}</div>
            <div className="text-[9px] text-stone-500 uppercase">Total</div>
          </div>
          <div className="p-3 rounded-xl bg-white border border-stone-200 text-center">
            <div className="text-lg font-bold text-emerald-600">{activeCount}</div>
            <div className="text-[9px] text-stone-500 uppercase">Active</div>
          </div>
          <div className="p-3 rounded-xl bg-white border border-stone-200 text-center">
            <div className="text-lg font-bold text-admin-primary">{invitedCount}</div>
            <div className="text-[9px] text-stone-500 uppercase">Invited</div>
          </div>
          <div className="p-3 rounded-xl bg-white border border-stone-200 text-center">
            <div className="text-lg font-bold text-stone-900">{templates.length}</div>
            <div className="text-[9px] text-stone-500 uppercase">Templates</div>
          </div>
        </div>

        {pageLoading && (
          <RubyLoader variant="section" label="Loading…" className="mb-4" />
        )}

        {/* Tab content */}
        {activeTab === 'staff' && renderStaffTab()}
        {activeTab === 'templates' && renderTemplatesTab()}
        {activeTab === 'workflows' && renderWorkflowsTab()}
        {activeTab === 'review' && renderReviewTab()}
      </div>

      {/* Web Form Builder Modal (QuickFieldList) */}
      {showFormBuilder && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white border border-stone-200 rounded-xl shadow-xl w-full max-w-6xl max-h-[95vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-3 border-b border-stone-200 bg-white rounded-t-xl sticky top-0 z-10">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-md bg-blue-600 flex items-center justify-center">
                  <ClipboardList className="w-4 h-4 text-white" />
                </div>
                <h3 className="text-base font-semibold text-stone-900">
                  {editingFormTemplate ? 'Edit Web Form' : 'New Web Form'}
                </h3>
              </div>
              <button
                onClick={() => { setShowFormBuilder(false); setEditingFormTemplate(null); }}
                className="p-2 text-stone-600 hover:text-stone-900 hover:bg-stone-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-4 sm:p-5">
              <QuickFieldList
                canUseAI={false}
                template={editingFormTemplate ? ({
                  id: editingFormTemplate.id,
                  name: editingFormTemplate.name,
                  description: editingFormTemplate.description,
                  category: 'other',
                  fields: (editingFormTemplate.formFields || []).map((f: any) => ({
                    id: f.id,
                    type: f.type,
                    label: f.label,
                    placeholder: f.placeholder,
                    required: f.required,
                    options: f.options,
                  })),
                  status: 'active',
                  requiresSignature: false,
                  createdAt: editingFormTemplate.createdAt || Date.now(),
                  updatedAt: Date.now(),
                } as any) : undefined}
                onSave={handleSaveFormTemplate}
                onCancel={() => { setShowFormBuilder(false); setEditingFormTemplate(null); }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Acknowledgment Editor Modal */}
      {showAckEditor && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-2 sm:p-4">
          <div className="bg-white border border-stone-200 rounded-xl shadow-xl w-full max-w-5xl max-h-[95vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-3 border-b border-stone-200 bg-white rounded-t-xl sticky top-0 z-10">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-md bg-rose-100 flex items-center justify-center">
                  <FileSignature className="w-4 h-4 text-rose-700" />
                </div>
                <h3 className="text-base font-semibold text-stone-900">
                  {editingAckTemplate ? 'Edit Acknowledgment' : 'New Acknowledgment'}
                </h3>
              </div>
              <button
                onClick={() => { setShowAckEditor(false); setEditingAckTemplate(null); }}
                className="p-2 text-stone-600 hover:text-stone-900 hover:bg-stone-100 rounded-lg transition-colors"
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-4 sm:p-5">
              <AcknowledgmentEditor
                canUseAI={false}
                initial={editingAckTemplate ?? undefined}
                onSave={handleSaveAck}
                onCancel={() => { setShowAckEditor(false); setEditingAckTemplate(null); }}
              />
            </div>
          </div>
        </div>
      )}

      {/* Type Picker Modal */}
      {showTypePicker && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/40" onClick={() => setShowTypePicker(false)} />
          <div className="relative bg-white border border-stone-200 rounded-2xl shadow-xl w-full max-w-lg p-5">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-stone-900">New Template — pick a type</h3>
              <button
                onClick={() => setShowTypePicker(false)}
                className="p-1.5 text-stone-600 hover:text-stone-900 hover:bg-stone-100 rounded-lg transition-colors"
              >
                <X size={18} />
              </button>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                onClick={startCreateAcknowledgment}
                className="text-left border-2 border-rose-300 rounded-xl p-3 cursor-pointer hover:bg-rose-50 transition-colors"
              >
                <div className="text-rose-700 font-semibold text-sm flex items-center gap-1.5">
                  <FileSignature size={16} /> Acknowledgment
                </div>
                <p className="text-[11px] text-stone-600 mt-1">Staff member reads text &amp; agrees/signs. (e.g. background-check authorization, handbook)</p>
              </button>
              <button
                type="button"
                onClick={startCreateFillableDoc}
                className="text-left border-2 border-amber-300 rounded-xl p-3 cursor-pointer hover:bg-amber-50 transition-colors"
              >
                <div className="text-amber-700 font-semibold text-sm flex items-center gap-1.5">
                  <FileType size={16} /> Fillable Document
                </div>
                <p className="text-[11px] text-stone-600 mt-1">You upload a blank PDF; the staff member downloads, fills, uploads it back. Best for IRS / bank / legal forms.</p>
              </button>
              <button
                type="button"
                onClick={startCreateWebForm}
                className="text-left border-2 border-blue-300 rounded-xl p-3 cursor-pointer hover:bg-blue-50 transition-colors"
              >
                <div className="text-blue-600 font-semibold text-sm flex items-center gap-1.5">
                  <ClipboardList size={16} /> Web Form
                </div>
                <p className="text-[11px] text-stone-600 mt-1">Fields filled online. Quick presets: Bank info, Emergency contact, Address…</p>
              </button>
              <button
                type="button"
                onClick={startCreateFileUpload}
                className="text-left border-2 border-stone-300 rounded-xl p-3 cursor-pointer hover:bg-stone-100 transition-colors"
              >
                <div className="text-stone-700 font-semibold text-sm flex items-center gap-1.5">
                  <Upload size={16} /> File Upload
                </div>
                <p className="text-[11px] text-stone-600 mt-1">Staff member uploads any document (PDF/JPG/PNG).</p>
              </button>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                onClick={() => setShowTypePicker(false)}
                className="px-3 py-2 text-sm text-stone-600 hover:bg-stone-100 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Fillable Document Create Modal */}
      {showFillableModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => !fillableSaving && !fillableUploading && setShowFillableModal(false)}
          />
          <div className="relative bg-white border border-stone-200 rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="bg-white text-stone-900 p-5 rounded-t-2xl border-b border-stone-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/20 flex items-center justify-center">
                    <FileType className="w-5 h-5 text-amber-700" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold">New Fillable Document</h3>
                    <p className="text-sm text-stone-600">Upload a blank PDF for staff members to complete</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowFillableModal(false)}
                  disabled={fillableSaving || fillableUploading}
                  className="p-2 hover:bg-stone-100 rounded-lg transition-colors disabled:opacity-50"
                >
                  <X size={20} />
                </button>
              </div>
            </div>

            <div className="p-5 space-y-5">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Template Name *</label>
                <input
                  type="text"
                  value={fillableForm.name}
                  onChange={(e) => setFillableForm((prev) => ({ ...prev, name: e.target.value }))}
                  className={INPUT}
                  placeholder="e.g., IRS W-4 2026"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Category</label>
                <SearchableDropdown
                  options={Object.entries(CATEGORY_LABELS).map(([value, label]) => ({ value, label: label as string }))}
                  value={fillableForm.category}
                  onChange={(val) => setFillableForm((prev) => ({ ...prev, category: (val || 'other') as DocumentCategory }))}
                  placeholder="Select category"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Description</label>
                <textarea
                  value={fillableForm.description}
                  onChange={(e) => setFillableForm((prev) => ({ ...prev, description: e.target.value }))}
                  rows={2}
                  className={`${INPUT} resize-none`}
                  placeholder="Brief instructions for the staff member..."
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-stone-700 mb-2">Blank Form (PDF) *</label>
                <label className={`block border-2 border-dashed rounded-lg p-4 text-center text-xs cursor-pointer transition-colors ${
                  fillableFileKey
                    ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
                    : 'border-amber-300 bg-amber-50 text-amber-700 hover:bg-amber-50'
                }`}
                >
                  <input
                    type="file"
                    accept="application/pdf"
                    className="hidden"
                    disabled={fillableUploading}
                    onChange={(e) => handleFillableFileChosen(e.target.files?.[0] || null)}
                  />
                  {fillableUploading ? (
                    <span className="inline-flex items-center gap-2">
                      <RubyGemMark size={14} drawing /> Uploading…
                    </span>
                  ) : fillableFileKey && fillableFile ? (
                    <span className="inline-flex items-center gap-2">
                      <CheckCircle size={14} /> {fillableFile.name} — ready
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-2">
                      <Upload size={14} /> Upload blank form (PDF) — staff members download this to complete
                    </span>
                  )}
                </label>
              </div>

              {fillableError && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 text-red-600 border border-red-200 text-sm">
                  <span>{fillableError}</span>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 p-5 border-t border-stone-200">
              <button
                onClick={() => setShowFillableModal(false)}
                disabled={fillableSaving || fillableUploading}
                className="px-5 py-2.5 bg-white border border-stone-300 text-stone-700 hover:bg-stone-50 rounded-lg font-medium transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveFillable}
                disabled={fillableSaving || fillableUploading || !fillableFileKey}
                className="px-5 py-2.5 bg-admin-primary hover:bg-admin-secondary text-white rounded-lg font-medium flex items-center gap-2 disabled:opacity-50 transition-colors"
              >
                {fillableSaving ? <RubyGemMark size={16} drawing color="currentColor" /> : <Save size={16} />}
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete confirms (custom — never native confirm) */}
      {renderConfirm(
        !!templateToDelete,
        'Delete Template',
        templateToDelete ? <>Are you sure you want to delete <strong className="text-stone-900">{templateToDelete.name}</strong>? This action cannot be undone.</> : '',
        confirmDeleteTemplate,
        () => { if (!loading) setTemplateToDelete(null); },
      )}
      {renderConfirm(
        !!workflowToDelete,
        'Delete Workflow',
        workflowToDelete ? <>Are you sure you want to delete <strong className="text-stone-900">{workflowToDelete.name}</strong>? This cannot be undone.</> : '',
        confirmDeleteWorkflow,
        () => { if (!loading) setWorkflowToDelete(null); },
      )}

      {/* Staff onboarding progress panel (slide-over) */}
      {selectedStaff && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40">
          <div className="bg-white border-l border-stone-200 w-full max-w-md h-full overflow-y-auto shadow-xl flex flex-col">
            <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-stone-200 bg-white sticky top-0 z-10">
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-stone-900 truncate">{selectedStaff.name}</h3>
                <p className="text-[11px] text-stone-500">Onboarding progress</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={() => setShowAssign(true)}
                  className="inline-flex items-center gap-1.5 bg-admin-primary hover:bg-admin-secondary text-white px-3 py-2 rounded-lg text-sm font-semibold transition-colors min-h-[44px]"
                >
                  <Plus size={16} /> Assign Workflow
                </button>
                <button
                  type="button"
                  onClick={() => setSelectedStaff(null)}
                  className="p-2 text-stone-600 hover:text-stone-900 hover:bg-stone-100 rounded-lg transition-colors min-h-[44px]"
                  aria-label="Close"
                >
                  <X size={20} />
                </button>
              </div>
            </div>
            <div className="p-4 sm:p-5 flex-1">
              <EmployeeProgressPanel
                employee={selectedStaff}
                refreshKey={progressRefreshKey}
              />
            </div>
          </div>
        </div>
      )}

      {/* Assign workflow modal */}
      {showAssign && selectedStaff && (
        <AssignWorkflowModal
          employee={selectedStaff}
          workflows={workflows}
          templates={templates}
          onAssign={async (workflowId: string) => {
            await staffDocumentRequests.startOnboarding(selectedStaff.id, workflowId);
            setProgressRefreshKey((k) => k + 1);
          }}
          onClose={() => setShowAssign(false)}
        />
      )}

      {viewTemplate && (
        <TemplateViewModal
          template={viewTemplate}
          onClose={() => setViewTemplate(null)}
          onEdit={() => {
            const t = viewTemplate;
            setViewTemplate(null);
            if (t) routeEditTemplate(t);
          }}
        />
      )}

      <InviteStaffModal
        open={showInvite}
        onClose={() => setShowInvite(false)}
        roles={roles}
        myLevels={levels}
        isOwner={isOwner}
        onInvite={handleInvite}
      />
      {renderTemplateModal()}
      {renderWorkflowModal()}
      {renderSubmissionModal()}
      {renderRejectModal()}
    </StaffPage>
  );
};

export default StaffManagementPage;
